import { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react'

const ToastContext = createContext(null)

const TOAST_COLORS = {
  error:   { bg: 'bg-red-50', border: 'border-red-300', text: 'text-red-800', icon: '✕', iconBg: 'bg-red-100 text-red-600' },
  warning: { bg: 'bg-amber-50', border: 'border-amber-300', text: 'text-amber-800', icon: '!', iconBg: 'bg-amber-100 text-amber-600' },
  success: { bg: 'bg-green-50', border: 'border-green-300', text: 'text-green-800', icon: '✓', iconBg: 'bg-green-100 text-green-600' },
  info:    { bg: 'bg-blue-50', border: 'border-blue-300', text: 'text-blue-800', icon: 'i', iconBg: 'bg-blue-100 text-blue-600' },
}

const DURATIONS = { error: 6000, warning: 5000, success: 4000, info: 4000 }
const MAX_TOASTS = 3

function Toast({ id, message, type, action, onDismiss }) {
  const colors = TOAST_COLORS[type] || TOAST_COLORS.info
  const [visible, setVisible] = useState(false)

  useEffect(() => {
    const frame = requestAnimationFrame(() => setVisible(true))
    return () => cancelAnimationFrame(frame)
  }, [])

  function handleDismiss() {
    setVisible(false)
    setTimeout(() => onDismiss(id), 200)
  }

  function handleAction() {
    if (action?.onClick) action.onClick()
    handleDismiss()
  }

  return (
    <div
      className={`flex items-start gap-3 max-w-sm w-full ${colors.bg} border ${colors.border} rounded-lg p-3 shadow-lg transition-all duration-200 ${visible ? 'translate-x-0 opacity-100' : 'translate-x-4 opacity-0'}`}
      role="alert"
    >
      <span className={`flex-shrink-0 w-5 h-5 flex items-center justify-center rounded-full text-xs font-bold ${colors.iconBg}`}>
        {colors.icon}
      </span>
      <p className={`text-sm ${colors.text} flex-1`}>{message}</p>
      {action && (
        <button
          onClick={handleAction}
          className={`flex-shrink-0 text-sm font-semibold underline underline-offset-2 ${colors.text} hover:opacity-70`}
        >
          {action.label}
        </button>
      )}
      <button
        onClick={handleDismiss}
        className="flex-shrink-0 text-slate-400 hover:text-slate-600 text-sm leading-none mt-0.5"
        aria-label="Dismiss"
      >
        ×
      </button>
    </div>
  )
}

let toastIdCounter = 0

export function ToastProvider({ children }) {
  const [toasts, setToasts] = useState([])
  const timersRef = useRef({})

  const dismissToast = useCallback((id) => {
    clearTimeout(timersRef.current[id])
    delete timersRef.current[id]
    setToasts(prev => prev.filter(t => t.id !== id))
  }, [])

  const showToast = useCallback((message, type = 'info', action = null) => {
    const id = ++toastIdCounter
    // Toasts with actions get extra time (6s) so user can click
    const duration = action ? 6000 : (DURATIONS[type] || 4000)

    setToasts(prev => {
      const next = [...prev, { id, message, type, action }]
      // Trim to MAX_TOASTS — remove oldest
      if (next.length > MAX_TOASTS) {
        const removed = next.shift()
        clearTimeout(timersRef.current[removed.id])
        delete timersRef.current[removed.id]
      }
      return next
    })

    timersRef.current[id] = setTimeout(() => dismissToast(id), duration)
    return id
  }, [dismissToast])

  useEffect(() => {
    const timers = timersRef.current
    return () => { Object.values(timers).forEach(clearTimeout) }
  }, [])

  return (
    <ToastContext.Provider value={{ showToast }}>
      {children}
      {/* Toast container — fixed bottom-right */}
      <div className="fixed bottom-4 right-4 z-50 flex flex-col gap-2 pointer-events-none no-print">
        {toasts.map(t => (
          <div key={t.id} className="pointer-events-auto">
            <Toast id={t.id} message={t.message} type={t.type} action={t.action} onDismiss={dismissToast} />
          </div>
        ))}
      </div>
    </ToastContext.Provider>
  )
}

// eslint-disable-next-line react-refresh/only-export-components
export function useToast() {
  const ctx = useContext(ToastContext)
  if (!ctx) throw new Error('useToast must be used within a ToastProvider')
  return ctx
}
